/**
 * Case-registratie & jaarverslag-audit voor VTP en BMW (migratie 167).
 * De codelijsten komen uit de API (src/constants/caseRegistratie.ts); de
 * backend weigert alles daarbuiten.
 */
import api from './api';

const d = <T,>(r: { data: { data: T } }) => r.data.data;
const BASIS = '/professional/case-registraties';

export type Discipline = 'VTP' | 'BMW';

export interface CodeLabel {
  code: string;
  label: string;
  omschrijving: string;
  groep?: string;
}

export interface RegistratieContext {
  discipline: Discipline;
  discipline_label: string;
  min_groep: number;
  codes: {
    categorieen: CodeLabel[];
    interventies: CodeLabel[];
    rolverhoudingen: CodeLabel[];
    /** Alleen VTP (H4.5, migratie 190); BMW: leeg. */
    geslachten_veroorzaker: CodeLabel[];
    uitsluit_redenen: CodeLabel[];
  };
  opdrachtgevers: Array<{ id: string; naam: string }>;
}

export interface Structuur {
  locaties: Array<{ id: string; naam: string }>;
  afdelingen: Array<{ id: string; naam: string; location_id: string | null }>;
  koppelingen: Array<{ soort: 'vp_melding' | 'dossier'; id: string; label: string }>;
}

export interface Registratie {
  id: string;
  case_id: string;
  case_code: string;
  gesprek_datum: string;
  is_nieuw_incident: boolean;
  categorieen: string[];
  rolverhouding: string | null;
  /** VTP H4.5 (migratie 190); alleen op de wortel. */
  geslacht_veroorzaker?: string | null;
  location_id: string | null;
  department_id: string | null;
  interventies: string[];
  koppeling_label: string | null;
  gecorrigeerd_op: string | null;
  /** Reden waarom de case buiten het jaarverslag is gehouden (geldt voor alle gesprekken van de case). */
  case_uitgesloten_reden: string | null;
  /** Jaar van de eerste registratie; kan bij een vervolggesprek vóór het audit-jaar liggen. */
  case_jaar: number;
  /** Nieuw incident dat een collega binnen het team al registreerde (migratie 174). */
  overgenomen_van_collega?: boolean;
  /** Ad-hoc werkvloergesprek (migratie 173): null = geen ad-hoc; alleen VERIFIED telt mee. */
  verificatie?: 'VERIFIED' | 'PENDING_VERIFICATION' | 'REJECTED' | null;
  verificatie_verloopt_op?: string | null;
}

export interface Telling {
  code: string;
  label: string;
  aantal: number;
}

export interface Analytics {
  min_groep: number;
  aantal_casussen: number | null;
  aantal_gesprekken: number;
  verborgen: string | null;
  aard: { totaal_kenmerken: number | null; verdeling: Telling[]; noot: string };
  rolverhouding: { verdeling: Telling[]; samengevoegd: number } | null;
  /** VTP H4.5; BMW null, ouder dan migratie 190 afwezig. */
  geslacht_veroorzaker?: { verdeling: Telling[]; samengevoegd: number } | null;
  afdelingen: { groepen: Array<{ label: string; aantal_casussen: number; per_categorie: Telling[] }>; samengevoegd_onder_algemeen: boolean };
  demografie: { aantal_opgaven: number; geslacht: Telling[] | null; leeftijd: Telling[] | null; reden_verborgen: string | null };
  interventies: Telling[];
}

export interface Audit {
  jaar: number;
  vergrendeld: boolean;
  vrijgegeven_op: string | null;
  registraties: Registratie[];
  preview: Analytics;
  /**
   * Vak B (`tekst`) en het werkvloeradvies uit Deel 5 (`advies`, platte tekst); alleen voor de professional.
   * `discriminatie`: de case heeft dat kenmerk (bronmateriaal voor VTP H5.1).
   */
  organisatie_signalen: Array<{
    id: string;
    datum: string;
    volgnummer: number;
    case_code: string;
    tekst: string | null;
    advies: string | null;
    discriminatie: boolean;
  }>;
}

export interface NieuweRegistratie {
  employer_id: string;
  gesprek_datum: string;
  is_nieuw_incident: boolean;
  case_id?: string | null;
  categorieen?: string[];
  rolverhouding?: string | null;
  geslacht_veroorzaker?: string | null;
  location_id?: string | null;
  department_id?: string | null;
  interventies: string[];
  vp_melding_id?: string | null;
  dossier_id?: string | null;
  overgenomen_van_collega?: boolean;
}

export type Correctie = Partial<
  Pick<
    NieuweRegistratie,
    'categorieen' | 'rolverhouding' | 'geslacht_veroorzaker' | 'location_id' | 'department_id' | 'interventies' | 'overgenomen_van_collega'
  >
>;

export const getContext = () => api.get<{ data: RegistratieContext }>(`${BASIS}/context`).then(d);
export const getStructuur = (employerId: string) => api.get<{ data: Structuur }>(`${BASIS}/opdrachtgevers/${employerId}/structuur`).then(d);
export const maakRegistratie = (body: NieuweRegistratie) => api.post<{ data: Registratie }>(BASIS, body).then(d);
export const corrigeer = (id: string, body: Correctie) => api.patch<{ data: Registratie }>(`${BASIS}/${id}`, body).then(d);
export const voegSamen = (id: string, doelCaseId: string) =>
  api.post<{ data: { case_id: string } }>(`${BASIS}/${id}/samenvoegen`, { doel_case_id: doelCaseId }).then(d);
export const zetUitsluiting = (id: string, reden: string | null) => api.post<{ data: Registratie }>(`${BASIS}/${id}/uitsluiten`, { reden }).then(d);
export const getAudit = (employerId: string, jaar: number) =>
  api.get<{ data: Audit }>(`${BASIS}/audit`, { params: { employer_id: employerId, jaar } }).then(d);
